import { useEffect, useState, type ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Dropdown } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface AccountRecord {
  id: number
  accountName?: string
  email?: string
  plan?: string
  billingCycle?: string
  amount?: number | string | null
  currency?: string
  renewalDate?: string | null
  status?: string
  notes?: string | null
}

interface SoftwareAccountFormProps {
  isOpen: boolean
  softwareId: number | null
  account?: AccountRecord | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  accountName: string
  email: string
  plan: string
  billingCycle: string
  amount: string
  currency: string
  renewalDate: string
  notes: string
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  )
}

const empty: FormData = {
  accountName: '',
  email: '',
  plan: '',
  billingCycle: 'monthly',
  amount: '',
  currency: 'INR',
  renewalDate: '',
  notes: '',
}

export function SoftwareAccountForm({
  isOpen,
  softwareId,
  account,
  onClose,
  onSuccess,
}: SoftwareAccountFormProps) {
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, reset, control } = useForm<FormData>({ defaultValues: empty })

  useEffect(() => {
    if (!isOpen) return
    if (!account) {
      reset(empty)
      return
    }
    reset({
      accountName: account.accountName || '',
      email: account.email || '',
      plan: account.plan || '',
      billingCycle: account.billingCycle || 'monthly',
      amount: account.amount == null ? '' : String(account.amount),
      currency: account.currency || 'INR',
      renewalDate: account.renewalDate ? String(account.renewalDate).slice(0, 10) : '',
      notes: account.notes || '',
    })
  }, [isOpen, account, reset])

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      const payload = {
        softwareId,
        accountName: data.accountName,
        email: data.email,
        plan: data.plan,
        billingCycle: data.billingCycle,
        amount: data.amount,
        currency: data.currency,
        renewalDate: data.renewalDate,
        notes: data.notes,
      }
      if (account) await apiClient.put(`/software/accounts/${account.id}`, payload)
      else await apiClient.post('/software/accounts', payload)
      toast.success(account ? 'Account updated' : 'Paid account added')
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not save the account')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={account ? 'Edit paid account' : 'Add paid account'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Account name">
            <Input {...register('accountName', { required: true, minLength: 2 })} placeholder="Design team" />
          </Field>
          <Field label="Account email">
            <Input type="email" {...register('email', { required: true })} placeholder="design@simplogics.com" />
          </Field>
          <Field label="Plan">
            <Input {...register('plan')} placeholder="Organization" />
          </Field>
          <Field label="Billing">
            <Controller
              name="billingCycle"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'yearly', label: 'Yearly' },
                    { value: 'one-time', label: 'One time' },
                  ]}
                />
              )}
            />
          </Field>
          <Field label="Amount">
            <Input type="number" min={0} step="0.01" {...register('amount')} placeholder="1200" />
          </Field>
          <Field label="Currency">
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'INR', label: 'INR' },
                    { value: 'USD', label: 'USD' },
                    { value: 'EUR', label: 'EUR' },
                  ]}
                />
              )}
            />
          </Field>
          <Field label="Renewal date">
            <Input type="date" {...register('renewalDate')} />
            <span className="block text-xs font-normal text-muted-foreground">
              The day this account renews. Monthly and yearly accounts show the next date from today.
            </span>
          </Field>
        </div>
        {account && (
          <p className="text-xs text-muted-foreground">
            Use Update status on the account page to mark it active, expired, or stopped. A reason is required.
          </p>
        )}
        <Field label="Notes">
          <textarea
            {...register('notes')}
            rows={3}
            placeholder="Seat count, invoice reference, or who owns the login"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading}>
            {loading && <LoadingSpinner size="sm" className="mr-2" />}
            {account ? 'Save account' : 'Add account'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
